import { logInfo } from "./logging-B7X3yL0T.js";
import { formatToolCallInstruction } from "./tool-call-formatting-ALc98KqF.js";
import { formatUserGeoSummary } from "./user-formatting-BrX0Xi05.js";
import { formatEventValue, formatKnownUserValue, getStringValue, isAggregateQuery } from "./utils-Dg2aeBEb.js";
import { formatDuration } from "./analyzer-CtmUqCwO.js";

//#region src/tools/support/search-events/formatters.ts
/**
* Format an explanation for how the input query was translated
*/
function formatExplanation(explanation) {
	return `## How I interpreted your query\n\n${explanation}`;
}
function formatInlineCode(value) {
	const backtickRuns = value.match(/`+/g) ?? [];
	const fenceLength = backtickRuns.reduce((max, run) => Math.max(max, run.length), 0) + 1;
	const fence = "`".repeat(fenceLength);
	const needsPadding = value.startsWith("`") || value.endsWith("`");
	return needsPadding ? `${fence} ${value} ${fence}` : `${fence}${value}${fence}`;
}
function formatExecutedTimeRange(timeRange) {
	if (!timeRange) return "Last 14d";
	if (timeRange.statsPeriod) return `Last ${timeRange.statsPeriod}`;
	if (timeRange.start && timeRange.end) return `${timeRange.start} to ${timeRange.end}`;
	return "Last 14d";
}
function formatExecutedSearch(executedSearch) {
	if (!executedSearch) return "";
	const fields = executedSearch.fields === void 0 ? void 0 : executedSearch.fields.length > 0 ? executedSearch.fields.map(formatInlineCode).join(", ") : "(none)";
	const lines = [
		"## Executed Search",
		`- Dataset: ${formatInlineCode(executedSearch.dataset)}`,
		`- Query: ${formatInlineCode(executedSearch.query || "(empty)")}`
	];
	if (fields !== void 0) lines.push(`- Fields: ${fields}`);
	if (executedSearch.sort) lines.push(`- Sort: ${formatInlineCode(executedSearch.sort)}`);
	lines.push(`- Time range: ${formatExecutedTimeRange(executedSearch.timeRange)}`);
	return `${lines.join("\n")}\n\n`;
}
function formatSearchPresentationHint(hint) {
	return `**Suggested presentation:** ${hint}\n\n`;
}
function formatSentryDashboardLink(url) {
	return `**View these results in Sentry**:\n${url}\nPlease tell the user this dashboard link is available if they want to open the results in Sentry.\n\n`;
}
function formatUserFieldLines(value, options = {}) {
	const prefix = options.prefix ?? "";
	const geoSummary = formatUserGeoSummary(value.geo);
	const userSummary = formatKnownUserValue(value, { includeGeo: false });
	const lines = [];
	if (userSummary) lines.push(`${prefix}**user**: ${userSummary}`);
	else if (!geoSummary) lines.push(`${prefix}**user**: ${formatEventValue(value)}`);
	if (geoSummary) lines.push(`${prefix}**user.geo**: ${geoSummary}`);
	return lines;
}
/**
* Format error event results for display
*/
function formatErrorResults(params) {
	const { eventData, inputQuery, includeExplanation, apiService, organizationSlug, explorerUrl, sentryQuery, fields, explanation } = params;
	let output = `# Search Results for "${inputQuery}"\n\n`;
	if (isAggregateQuery(fields)) output += formatSearchPresentationHint("A compact table works well for these aggregate results.");
	else output += formatSearchPresentationHint("Useful details to surface include severity, event IDs, and links.");
	if (includeExplanation && explanation) {
		output += formatExplanation(explanation);
		output += `\n\n`;
	}
	output += formatExecutedSearch(params.executedSearch);
	output += formatSentryDashboardLink(explorerUrl);
	if (eventData.length === 0) {
		logInfo(`No error events found for query: ${inputQuery}`, { extra: {
			query: sentryQuery,
			fields,
			organizationSlug,
			dataset: "errors"
		} });
		output += `No results found.\n\n`;
		output += `Try being more specific or using different terms in your search.\n`;
		return output;
	}
	output += `Found ${eventData.length} ${isAggregateQuery(fields) ? "aggregate result" : "error"}${eventData.length === 1 ? "" : "s"}:\n\n`;
	if (isAggregateQuery(fields)) {
		output += "```json\n";
		output += JSON.stringify(eventData, null, 2);
		output += "\n```\n\n";
	} else {
		const priorityFields = [
			"title",
			"issue",
			"project",
			"level",
			"error.type",
			"message",
			"culprit",
			"timestamp",
			"last_seen()",
			"count()"
		];
		for (const event of eventData) {
			const title = getStringValue(event, "title") || getStringValue(event, "message") || getStringValue(event, "error.value") || "Error Event";
			output += `## ${title}\n\n`;
			for (const field of priorityFields) if (field in event && event[field] !== null && event[field] !== void 0) {
				const value = event[field];
				if (field === "issue" && typeof value === "string") {
					output += `**Issue ID**: ${value}\n`;
					output += `**Issue URL**: ${apiService.getIssueUrl(organizationSlug, value)}\n`;
				} else if (field === "issue") output += `**Issue ID**: ${formatEventValue(value)}\n`;
				else output += `**${field}**: ${formatEventValue(value)}\n`;
			}
			const displayedFields = new Set([...priorityFields, "id"]);
			for (const [key, value] of Object.entries(event)) if (!displayedFields.has(key) && value !== null && value !== void 0) {
				if (key === "user" && typeof value === "object" && value !== null) {
					for (const line of formatUserFieldLines(value)) output += `${line}\n`;
					continue;
				}
				output += `**${key}**: ${formatEventValue(value)}\n`;
			}
			output += "\n";
		}
	}
	output += "## Next Steps\n\n";
	output += "- Get more details about a specific error: Use the Issue ID\n";
	output += "- View error groups: Navigate to the Issues page in Sentry\n";
	output += "- Set up alerts: Configure alert rules for these error patterns\n";
	return output;
}
/**
* Format log event results for display
*/
function formatLogResults(params) {
	const { eventData, inputQuery, includeExplanation, apiService, organizationSlug, explorerUrl, sentryQuery, fields, explanation } = params;
	let output = `# Search Results for "${inputQuery}"\n\n`;
	if (isAggregateQuery(fields)) output += formatSearchPresentationHint("A compact table works well for these aggregate results.");
	else output += formatSearchPresentationHint("Console-style formatting works well for these logs, with timestamps preserved.");
	if (includeExplanation && explanation) {
		output += formatExplanation(explanation);
		output += `\n\n`;
	}
	output += formatExecutedSearch(params.executedSearch);
	output += formatSentryDashboardLink(explorerUrl);
	if (eventData.length === 0) {
		logInfo(`No log events found for query: ${inputQuery}`, { extra: {
			query: sentryQuery,
			fields,
			organizationSlug,
			dataset: "logs"
		} });
		output += `No results found.\n\n`;
		output += `Try being more specific or using different terms in your search.\n`;
		return output;
	}
	output += `Found ${eventData.length} ${isAggregateQuery(fields) ? "aggregate result" : "log"}${eventData.length === 1 ? "" : "s"}:\n\n`;
	if (isAggregateQuery(fields)) {
		output += "```json\n";
		output += JSON.stringify(eventData, null, 2);
		output += "\n```\n\n";
	} else {
		output += "```console\n";
		for (const event of eventData) {
			const timestamp = getStringValue(event, "timestamp", "N/A");
			const severity = getStringValue(event, "severity", "info");
			const message = getStringValue(event, "message", "No message");
			const severityUpper = severity.toUpperCase();
			const severityEmojis = {
				ERROR: "🔴",
				FATAL: "🔴",
				WARN: "🟡",
				WARNING: "🟡",
				INFO: "🔵",
				DEBUG: "⚫",
				TRACE: "⚫"
			};
			const severityEmoji = severityEmojis[severityUpper] || "🔵";
			output += `${timestamp} ${severityEmoji} [${severityUpper.padEnd(5)}] ${message}\n`;
		}
		output += "```\n\n";
		output += "## Log Details\n\n";
		const priorityFields = [
			"message",
			"severity",
			"severity_number",
			"timestamp",
			"project",
			"trace",
			"sentry.item_id"
		];
		for (let i = 0; i < eventData.length; i++) {
			const event = eventData[i];
			output += `### Log ${i + 1}\n`;
			for (const field of priorityFields) if (field in event && event[field] !== null && event[field] !== void 0) {
				const value = event[field];
				if (field === "trace" && typeof value === "string") {
					output += `- **Trace ID**: ${value}\n`;
					output += `- **Trace URL**: ${apiService.getTraceUrl(organizationSlug, value)}\n`;
				} else output += `- **${field}**: ${formatEventValue(value)}\n`;
			}
			const displayedFields = new Set([...priorityFields, "id"]);
			for (const [key, value] of Object.entries(event)) if (!displayedFields.has(key) && value !== null && value !== void 0) {
				if (key === "user" && typeof value === "object" && value !== null) {
					for (const line of formatUserFieldLines(value, { prefix: "- " })) output += `${line}\n`;
					continue;
				}
				output += `- **${key}**: ${formatEventValue(value)}\n`;
			}
			output += "\n";
		}
	}
	output += "## Next Steps\n\n";
	output += "- View related traces: Click on the Trace URL if available\n";
	output += "- Filter by severity: Adjust your query to focus on specific log levels\n";
	output += "- Export logs: Use the Sentry web interface for bulk export\n";
	return output;
}
/**
* Format span/trace event results for display
*/
function formatSpanResults(params) {
	const { eventData, inputQuery, includeExplanation, apiService, organizationSlug, explorerUrl, sentryQuery, fields, explanation } = params;
	let output = `# Search Results for "${inputQuery}"\n\n`;
	if (isAggregateQuery(fields)) output += formatSearchPresentationHint("A compact table works well for these aggregate results.");
	else output += formatSearchPresentationHint("A timeline works well for these traces, with durations and parent-child span relationships visible.");
	if (includeExplanation && explanation) {
		output += formatExplanation(explanation);
		output += `\n\n`;
	}
	output += formatExecutedSearch(params.executedSearch);
	output += formatSentryDashboardLink(explorerUrl);
	if (eventData.length === 0) {
		logInfo(`No span events found for query: ${inputQuery}`, { extra: {
			query: sentryQuery,
			fields,
			organizationSlug,
			dataset: "spans"
		} });
		output += `No results found.\n\n`;
		output += `Try being more specific or using different terms in your search.\n`;
		return output;
	}
	output += `Found ${eventData.length} ${isAggregateQuery(fields) ? `aggregate result${eventData.length === 1 ? "" : "s"}` : `trace${eventData.length === 1 ? "" : "s"}/span${eventData.length === 1 ? "" : "s"}`}:\n\n`;
	if (isAggregateQuery(fields)) {
		output += "```json\n";
		output += JSON.stringify(eventData, null, 2);
		output += "\n```\n\n";
	} else {
		const priorityFields = [
			"id",
			"span.op",
			"span.description",
			"transaction",
			"span.duration",
			"span.status",
			"trace",
			"project",
			"timestamp"
		];
		for (const event of eventData) {
			const title = getStringValue(event, "span.description") || getStringValue(event, "transaction") || getStringValue(event, "span.op") || "Span";
			output += `## ${title}\n\n`;
			for (const field of priorityFields) if (field in event && event[field] !== null && event[field] !== void 0) {
				const value = event[field];
				if (field === "trace" && typeof value === "string") {
					output += `**Trace ID**: ${value}\n`;
					output += `**Trace URL**: ${apiService.getTraceUrl(organizationSlug, value)}\n`;
				} else if (field === "span.duration" && typeof value === "number") output += `**${field}**: ${value}ms\n`;
				else output += `**${field}**: ${formatEventValue(value)}\n`;
			}
			const displayedFields = new Set([...priorityFields, "id"]);
			for (const [key, value] of Object.entries(event)) if (!displayedFields.has(key) && value !== null && value !== void 0) {
				if (key === "user" && typeof value === "object" && value !== null) {
					for (const line of formatUserFieldLines(value)) output += `${line}\n`;
					continue;
				}
				output += `**${key}**: ${formatEventValue(value)}\n`;
			}
			output += "\n";
		}
	}
	output += "## Next Steps\n\n";
	output += "- View the full trace: Click on the Trace URL above\n";
	output += "- Search for related spans: Modify your query to be more specific\n";
	output += "- Export data: Use the Sentry web interface for advanced analysis\n";
	return output;
}
function getProfileDurationLabel(field, value) {
	if ((field === "transaction.duration" || field === "profile.duration") && typeof value === "number") return formatDuration(value);
	return void 0;
}
function getProfileProject(event) {
	return getStringValue(event, "project") || null;
}
function getProfileTimestampValue(value) {
	if (typeof value === "string") {
		const trimmedValue = value.trim();
		return trimmedValue.length > 0 ? trimmedValue : null;
	}
	return null;
}
function getProfileDetailUrl(apiService, organizationSlug, event) {
	const project = getProfileProject(event);
	if (!project) return null;
	const profileId = getStringValue(event, "profile.id");
	if (profileId) return apiService.getProfileUrl(organizationSlug, project, profileId);
	const profilerId = getStringValue(event, "profiler.id");
	const start = getProfileTimestampValue(event["precise.start_ts"]);
	const end = getProfileTimestampValue(event["precise.finish_ts"]);
	if (profilerId && start && end) return apiService.getContinuousProfileUrl(organizationSlug, project, {
		profilerId,
		start,
		end
	});
	return null;
}
function formatProfileResults(params) {
	const { eventData, inputQuery, includeExplanation, apiService, organizationSlug, explorerUrl, sentryQuery, fields, explanation } = params;
	let output = `# Search Results for "${inputQuery}"\n\n`;
	if (isAggregateQuery(fields)) output += formatSearchPresentationHint("A compact table with readable duration units works well for these profile aggregates.");
	else output += formatSearchPresentationHint("Concise summaries work well for these profiles, highlighting profile ID, transaction, duration, release, and trace context.");
	if (includeExplanation && explanation) {
		output += formatExplanation(explanation);
		output += `\n\n`;
	}
	output += formatExecutedSearch(params.executedSearch);
	output += formatSentryDashboardLink(explorerUrl);
	if (eventData.length === 0) {
		logInfo(`No profile events found for query: ${inputQuery}`, { extra: {
			query: sentryQuery,
			fields,
			organizationSlug,
			dataset: "profiles"
		} });
		output += "No results found.\n\n";
		output += "Try narrowing the transaction, release, platform, or profile filters.\n";
		return output;
	}
	output += `Found ${eventData.length} ${isAggregateQuery(fields) ? `aggregate result${eventData.length === 1 ? "" : "s"}` : `profile${eventData.length === 1 ? "" : "s"}`}:\n\n`;
	if (isAggregateQuery(fields)) {
		output += "```json\n";
		output += JSON.stringify(eventData, null, 2);
		output += "\n```\n\n";
	} else {
		const priorityFields = [
			"profile.id",
			"profiler.id",
			"thread.id",
			"transaction",
			"timestamp",
			"transaction.duration",
			"release",
			"environment",
			"project",
			"trace",
			"precise.start_ts",
			"precise.finish_ts"
		];
		for (const event of eventData) {
			const title = getStringValue(event, "transaction") || getStringValue(event, "profile.id") || getStringValue(event, "profiler.id") || "Profile";
			const detailUrl = getProfileDetailUrl(apiService, organizationSlug, event);
			output += `## ${title}\n\n`;
			if (detailUrl) output += `**Profile URL**: ${detailUrl}\n`;
			for (const field of priorityFields) if (field in event && event[field] !== null && event[field] !== void 0) {
				const value = event[field];
				const durationLabel = getProfileDurationLabel(field, value);
				if (field === "trace" && typeof value === "string") {
					output += `**Trace ID**: ${value}\n`;
					output += `**Trace URL**: ${apiService.getTraceUrl(organizationSlug, value)}\n`;
				} else if (durationLabel) output += `**${field}**: ${durationLabel}\n`;
				else output += `**${field}**: ${formatEventValue(value)}\n`;
			}
			const displayedFields = new Set([...priorityFields, "id"]);
			for (const [key, value] of Object.entries(event)) if (!displayedFields.has(key) && value !== null && value !== void 0) {
				if (key === "user" && typeof value === "object" && value !== null) {
					for (const line of formatUserFieldLines(value)) output += `${line}\n`;
					continue;
				}
				output += `**${key}**: ${formatEventValue(value)}\n`;
			}
			output += "\n";
		}
	}
	output += "## Next Steps\n\n";
	if (params.experimentalMode === true) output += "- Open a Profile URL above when available, or use `get_sentry_resource` with the profile URL for the full detail view\n";
	else {
		const profileDetailsInstruction = formatToolCallInstruction({
			toolName: "get_profile_details",
			arguments: {
				organizationSlug,
				projectSlugOrId: "<project>",
				profileId: "<profile_id>"
			},
			experimentalMode: false,
			availableToolNames: params.availableToolNames,
			directToolNames: params.directToolNames,
			fallbackInstruction: "Profile detail lookup is not available in this session",
			purpose: "for the full detail view"
		});
		output += `- Open a Profile URL above when available. ${profileDetailsInstruction}\n`;
	}
	output += "- Open the Trace URL for an end-to-end view of the profiled request when available\n";
	output += "- Refine the profiling search in Sentry by transaction, release, platform, or environment\n";
	return output;
}
/**
* Format trace metric results for display
*/
function formatTraceMetricsResults(params) {
	const { eventData, inputQuery, includeExplanation, apiService, organizationSlug, explorerUrl, sentryQuery, fields, explanation } = params;
	let output = `# Search Results for "${inputQuery}"\n\n`;
	if (isAggregateQuery(fields)) output += formatSearchPresentationHint("A compact table with grouping labels and units works well for these metric aggregates.");
	else output += formatSearchPresentationHint("Concise summaries work well for these metric samples, highlighting metric name, type, value, and trace context.");
	if (includeExplanation && explanation) {
		output += formatExplanation(explanation);
		output += `\n\n`;
	}
	output += formatExecutedSearch(params.executedSearch);
	output += formatSentryDashboardLink(explorerUrl);
	if (eventData.length === 0) {
		logInfo(`No trace metric events found for query: ${inputQuery}`, { extra: {
			query: sentryQuery,
			fields,
			organizationSlug,
			dataset: "tracemetrics"
		} });
		output += "No results found.\n\n";
		output += "Try being more specific about the metric name, type, or filters.\n";
		return output;
	}
	output += `Found ${eventData.length} ${isAggregateQuery(fields) ? `aggregate result${eventData.length === 1 ? "" : "s"}` : `metric sample${eventData.length === 1 ? "" : "s"}`}:\n\n`;
	if (isAggregateQuery(fields)) {
		output += "```json\n";
		output += JSON.stringify(eventData, null, 2);
		output += "\n```\n\n";
	} else {
		const priorityFields = [
			"metric.name",
			"metric.type",
			"metric.unit",
			"value",
			"project",
			"timestamp",
			"trace",
			"span_id"
		];
		for (const event of eventData) {
			const title = getStringValue(event, "metric.name") || getStringValue(event, "trace") || "Metric Sample";
			output += `## ${title}\n\n`;
			for (const field of priorityFields) if (field in event && event[field] !== null && event[field] !== void 0) {
				const value = event[field];
				if (field === "trace" && typeof value === "string") {
					output += `**Trace ID**: ${value}\n`;
					output += `**Trace URL**: ${apiService.getTraceUrl(organizationSlug, value)}\n`;
				} else output += `**${field}**: ${formatEventValue(value)}\n`;
			}
			const displayedFields = new Set([...priorityFields, "id"]);
			for (const [key, value] of Object.entries(event)) if (!displayedFields.has(key) && value !== null && value !== void 0) {
				if (key === "user" && typeof value === "object" && value !== null) {
					for (const line of formatUserFieldLines(value)) output += `${line}\n`;
					continue;
				}
				output += `**${key}**: ${formatEventValue(value)}\n`;
			}
			output += "\n";
		}
	}
	output += "## Next Steps\n\n";
	output += "- Open the Metrics page link above to refine the selected metric\n";
	output += "- Drill into a specific sample by opening its Trace URL or using `get_sentry_resource` with that trace ID\n";
	output += "- Metrics do not expose a standalone detail resource here; use the related trace for deeper inspection\n";
	output += "- Group by additional attributes to break down the metric further\n";
	output += "- Switch between samples and aggregates in Sentry for deeper analysis\n";
	return output;
}

//#endregion
export { formatErrorResults, formatExecutedSearch, formatExplanation, formatLogResults, formatProfileResults, formatSearchPresentationHint, formatSentryDashboardLink, formatSpanResults, formatTraceMetricsResults };
//# sourceMappingURL=formatters-Cuh2gD9M.js.map